import React, { useEffect } from 'react';
import { Routes, Route, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { CssBaseline, ThemeProvider } from '@mui/material';

import { colorModeContext, useMode } from './theme';

import Sidebar from './scenes/global/Sidebar';
import Topbar from './scenes/global/Topbar';

import Dashboard from './scenes/dashboard';
import AgentsPage from './scenes/Agents/AgentsPage';
import NewAgentPage from './scenes/Agents/NewAgentPage';
import AgentDetailPage from './scenes/Agents/AgentDetailPage';
import ScenarioBuilder from './scenes/ScenarioBuilder';
import CalendarPage from './scenes/Calendar';
import ChatPage from './scenes/Chat';
import KeysPage from './scenes/Keys';
import AgentConfigPage from './scenes/AgentConfigPage';
import OrganizationPage from './scenes/Organization';

import LoginPage from './scenes/Login';
import RegisterPage from './scenes/Register';
import PrivateRoute from './components/PrivateRoute';
import PhoneNumbers from './scenes/PhoneNumbers';
import Files from './scenes/Files';
import Tools from './scenes/Tools';
import CallLogs from './scenes/CallLogs';
import ApiLogs from './scenes/ApiLogs';
import WebhookLogs from './scenes/WebhookLogs';
import VoiceLibrary from './scenes/VoiceLibrary';
import AdminRegistrationRequestsPage from './scenes/AdminRegistrationRequests';
import AuditLogsPage from './scenes/AuditLogs';
import AiKeysPage from './scenes/AiKeys';
import StudentsPage from './scenes/Students';
import ClassesPage from './scenes/Classes';
import InstructorsPage from './scenes/Instructors';
import ReportsPage from './scenes/Reports';
import RostersPage from './scenes/Rosters';
import PaymentsPage from './scenes/Payments';
import IntakePage from './scenes/Intake';
import RecentsPage from './scenes/Recents';
import PublicEnrollmentPage from './scenes/PublicEnrollment';
import {
  AUTH_TIMEOUT_MS,
  clearAuth,
  getAuthAgeMs,
  isAuthenticated,
  isAdmin
} from './lib/auth';

function AuthTimeoutWatcher() {
  const navigate = useNavigate();

  useEffect(() => {
    const enforceTimeout = () => {
      if (!isAuthenticated()) {
        clearAuth();
        navigate('/login', { replace: true });
      }
    };

    enforceTimeout();

    const age = getAuthAgeMs();
    const remaining = age === null ? 0 : Math.max(AUTH_TIMEOUT_MS - age, 0);
    const timeoutId = window.setTimeout(enforceTimeout, remaining);
    const intervalId = window.setInterval(enforceTimeout, 60 * 1000);

    return () => {
      window.clearTimeout(timeoutId);
      window.clearInterval(intervalId);
    };
  }, [navigate]);

  return null;
}

function AdminRoute({ children }) {
  return isAdmin() ? children : <Navigate to="" replace />;
}

function ProtectedLayout() {
  return (
    <div className="app">
      <AuthTimeoutWatcher />
      <Sidebar />
      <main className="content">
        <Topbar />
        <Outlet />
      </main>
    </div>
  );
}

export default function App() {
  const [theme, colorMode] = useMode();

  return (
    <colorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />

        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/enroll" element={<PublicEnrollmentPage />} />
          <Route path="/payment-complete" element={<PublicEnrollmentPage />} />

          <Route
            element={
              <PrivateRoute>
                <ProtectedLayout />
              </PrivateRoute>
            }
          >
            <Route index element={<IntakePage />} />
            <Route path="dashboard" element={<Dashboard />} />

            <Route path="agents" element={<AgentsPage />} />
            <Route path="agents/new" element={<NewAgentPage />} />
            <Route path="agents/:id" element={<AgentDetailPage />} />
            <Route path="students" element={<StudentsPage />} />
            <Route path="classes" element={<ClassesPage />} />

            <Route path="scenario-builder" element={<ScenarioBuilder />} />
            <Route path="calendar" element={<CalendarPage enableRecurrence />} />
            <Route path="chat" element={<ChatPage />} />
            <Route path="keys" element={<KeysPage />} />
            <Route path="agent-config" element={<AgentConfigPage />} />
            <Route path="organization" element={<OrganizationPage />} />
            <Route path="phone-numbers" element={<PhoneNumbers />} />
            <Route path="files" element={<Files />} />
            <Route path="tools" element={<Tools />} />
            <Route path="call-logs" element={<CallLogs />} />
            <Route path="api-logs" element={<ApiLogs />} />
            <Route path="webhook-logs" element={<WebhookLogs />} />
            <Route path="voice-library" element={<VoiceLibrary />} />
            <Route path="instructors" element={<InstructorsPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="rosters" element={<RostersPage />} />
            <Route path="payments" element={<PaymentsPage />} />
            <Route path="calendar-intake" element={<Navigate to="/" replace />} />
            <Route path="recents" element={<RecentsPage />} />
            
            <Route
              path="admin/registration-requests"
              element={
                <AdminRoute>
                  <AdminRegistrationRequestsPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/audit-logs"
              element={
                <AdminRoute>
                  <AuditLogsPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/ai-keys"
              element={
                <AdminRoute>
                  <AiKeysPage />
                </AdminRoute>
              }
            />

            <Route path="*" element={<Navigate to="" replace />} />
          </Route>
        </Routes>
      </ThemeProvider>
    </colorModeContext.Provider>
  );
}